import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, Check, X, Star, Eye, EyeOff, Megaphone, Loader2, MapPin, Globe } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const adTypes = [
  { value: 'business', label: 'Business' },
  { value: 'announcement', label: 'Announcement' },
  { value: 'sponsored', label: 'Sponsored' },
];

const categories = [
  { value: 'restaurant', label: 'Restaurant / Food' },
  { value: 'store', label: 'Retail Store' },
  { value: 'service', label: 'Professional Service' },
  { value: 'gurudwara', label: 'Gurudwara' },
  { value: 'other', label: 'Other' },
];

const AdminAdvertisements = () => {
  const { token } = useAuth();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');
  
  const [form, setForm] = useState({
    ad_type: 'business',
    title: '',
    description: '',
    business_name: '',
    category: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    website: '',
    image_url: '',
    is_featured: false
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAds = async (status) => {
    setLoading(true);
    try {
      const url = status === 'all' 
        ? `${API_URL}/admin/advertisements`
        : `${API_URL}/admin/advertisements?status=${status}`;
      const response = await axios.get(url, { headers });
      setAds(response.data);
    } catch (error) {
      toast.error('Failed to fetch advertisements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAds(filter);
  }, [filter]);

  const resetForm = () => {
    setForm({
      ad_type: 'business',
      title: '',
      description: '',
      business_name: '',
      category: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      website: '',
      image_url: '',
      is_featured: false
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.post(`${API_URL}/admin/advertisements`, form, { headers });
      toast.success('Advertisement created');
      setDialogOpen(false);
      resetForm();
      fetchAds(filter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ad');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (adId) => {
    try {
      await axios.patch(`${API_URL}/admin/advertisements/${adId}/approve`, {}, { headers });
      toast.success('Advertisement approved');
      fetchAds(filter);
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const handleReject = async (adId) => {
    try {
      await axios.patch(`${API_URL}/admin/advertisements/${adId}/reject`, {}, { headers });
      toast.success('Advertisement rejected');
      fetchAds(filter);
    } catch (error) {
      toast.error('Failed to reject');
    }
  };

  const toggleActive = async (adId) => {
    try {
      const response = await axios.patch(`${API_URL}/admin/advertisements/${adId}/toggle-active`, {}, { headers });
      toast.success(response.data.is_active ? 'Ad activated' : 'Ad deactivated');
      fetchAds(filter);
    } catch (error) {
      toast.error('Failed to toggle');
    }
  };

  const toggleFeatured = async (adId) => {
    try {
      const response = await axios.patch(`${API_URL}/admin/advertisements/${adId}/toggle-featured`, {}, { headers });
      toast.success(response.data.is_featured ? 'Now featured' : 'Removed from featured');
      fetchAds(filter);
    } catch (error) {
      toast.error('Failed to toggle');
    }
  };

  const handleDelete = async (adId) => {
    if (!window.confirm('Delete this advertisement?')) return;
    try {
      await axios.delete(`${API_URL}/admin/advertisements/${adId}`, { headers });
      toast.success('Advertisement deleted');
      fetchAds(filter);
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const getStatusBadge = (ad) => {
    if (ad.status === 'pending') return <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>;
    if (ad.status === 'rejected') return <Badge className="bg-red-100 text-red-700">Rejected</Badge>;
    if (!ad.is_active) return <Badge className="bg-gray-100 text-gray-700">Inactive</Badge>;
    return <Badge className="bg-green-100 text-green-700">Active</Badge>;
  };

  return (
    <div data-testid="admin-advertisements">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-gray-900">Advertisements</h1>
          <p className="text-gray-600">Manage business listings and announcements</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-ad-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Ad
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Create Advertisement</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Ad Type</Label>
                <Select value={form.ad_type} onValueChange={(val) => setForm({ ...form, ad_type: val })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {adTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {form.ad_type === 'business' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Business Name</Label>
                    <Input
                      value={form.business_name}
                      onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                      placeholder="Business name"
                    />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={form.category} onValueChange={(val) => setForm({ ...form, category: val })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <div>
                <Label>Title *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label>Description *</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Contact Name</Label>
                  <Input
                    value={form.contact_name}
                    onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Contact Email</Label>
                  <Input
                    type="email"
                    value={form.contact_email}
                    onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={form.contact_phone}
                    onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Website</Label>
                  <Input
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label>Address</Label>
                <Input
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                />
              </div>

              <div>
                <Label>Image URL</Label>
                <Input
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                />
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  checked={form.is_featured}
                  onCheckedChange={(checked) => setForm({ ...form, is_featured: checked })}
                />
                <Label>Featured (shows on homepage)</Label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 btn-primary" disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Create
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={filter} onValueChange={setFilter} className="mb-6">
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-saffron-500" />
        </div>
      ) : ads.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Megaphone className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600">No advertisements</h3>
            <p className="text-gray-500">Create your first ad or wait for submissions</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {ads.map((ad) => (
            <Card key={ad.id} className="hover:shadow-md transition-shadow" data-testid={`ad-row-${ad.id}`}>
              <CardContent className="p-4">
                <div className="flex gap-4">
                  {ad.image_url ? (
                    <img src={ad.image_url} alt={ad.title} className="w-20 h-20 rounded-lg object-cover" />
                  ) : (
                    <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Megaphone className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {getStatusBadge(ad)}
                      {ad.is_featured && <Badge className="bg-gold-100 text-gold-700"><Star className="w-3 h-3 mr-1" />Featured</Badge>}
                      <Badge variant="outline" className="capitalize">{ad.ad_type}</Badge>
                    </div>
                    <h3 className="font-semibold text-gray-900">{ad.business_name || ad.title}</h3>
                    <p className="text-sm text-gray-500 line-clamp-1">{ad.description}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                      {ad.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{ad.address}</span>}
                      {ad.website && <span className="flex items-center gap-1"><Globe className="w-3 h-3" />Website</span>}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    {ad.status === 'pending' && (
                      <div className="flex gap-1">
                        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleApprove(ad.id)}>
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleReject(ad.id)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    {ad.status === 'approved' && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => toggleActive(ad.id)} title={ad.is_active ? 'Deactivate' : 'Activate'}>
                          {ad.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => toggleFeatured(ad.id)} title={ad.is_featured ? 'Remove featured' : 'Make featured'}>
                          <Star className={`w-4 h-4 ${ad.is_featured ? 'fill-gold-500 text-gold-500' : ''}`} />
                        </Button>
                      </div>
                    )}
                    <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDelete(ad.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminAdvertisements;
